import { FloatingCard } from '@/components/FloatingCard';
import FullScreenImageModal from '@/components/FullscreenImageModal';
import Timer from '@/components/Timer';
import Constants from '@/Constants';
import { useQuestionnaire } from '@/hooks/questionnaire/useQuestionnaire';
import { Alternative, Answer, AnswerJSONDataItem, AnswerOption } from '@/models/Question';
import { Questionnaire } from '@/models/Questionnaire';
import { shuffle } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { finishQuestionnaireRequest, getQuestionRequest, updateAnswerRequest } from '@/store/questionnaire/action';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NavButton from './NavButton';
import NavigateQuestionModal from './NavigateQuestionModal';
import './style.scss';

interface PropsType {
    questionnaireId: number;
    finished: CallableFunction;
}

const QuestionnaireQuestions = ({ finished, questionnaireId }: PropsType) => {
    const { question, strings, answeredQuestion, successOnFinishQuestionnaire } = useSelector((state: ApplicationState) => ({
        answeredQuestion: state.questionnaire.answered,
        question: state.questionnaire.question,
        strings: state.locale.data,
        successOnFinishQuestionnaire: state.questionnaire.successOnFinishQuestionnaire,
    }));
    const dispatch = useDispatch();

    const [showNavigation, setShowNavigation] = useState(false);

    const [currentAnswer, setCurrentAnswer] = useState<Answer>({} as Answer);
    const [answerIndex, setAnswerIndex] = useState(1);
    const [qtyAnswered, setQtyAnswered] = useState(0);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [alternatives, setAlternatives] = useState<AnswerOption[]>([]);
    const [questions, setQuestions] = useState<Alternative[]>([]);
    const [optionsLoaded, setOptionsLoaded] = useState<boolean>(false);
    const [selectedOptionnIds, setSelectedOptionIds] = useState<number[]>([]);
    const [fullScreenImage, setFullscreenImage] = useState<boolean>(false);

    const { questionnaire = {} as Questionnaire, serverCurrentDate, revalidateResult, mutate } = useQuestionnaire(questionnaireId);

    useEffect(() => {
        if (serverCurrentDate && questionnaire.id && questionnaire.expirationDate != null) {
            const miliLeft: number = questionnaire.expirationDate - serverCurrentDate;
            const minutesLeft = miliLeft / 60000;
            if (minutesLeft <= 0) handleConcluded();
            setTimeLeft(minutesLeft);
        }
    }, [questionnaire]);

    useEffect(() => {
        questionnaire.answers?.map((answer, index) => {
            if (answerIndex - 1 === index) {
                setCurrentAnswer(answer);
            }
        });

        const { answers } = questionnaire || [];

        const singleOptionAnswered: number = answers.filter(({ answerOption, answerJSON }) => answerOption && !answerJSON).length;
        const multiOptionAnswered: number = answers.filter(({ answerJSON }) => answerJSON?.answer?.some(({ data }) => data)).length;

        setQtyAnswered(singleOptionAnswered + multiOptionAnswered);
    }, [questionnaire.answers, answeredQuestion]);

    useEffect(() => {
        if (questionnaire.id && currentAnswer && currentAnswer.question && question.id !== currentAnswer.question.id) {
            dispatch(getQuestionRequest(questionnaire.id, currentAnswer.question.id));
        } else if (!currentAnswer.answerOption) {
            setSelectedOptionIds([]);
        } else if (!optionsLoaded) {
            setSelectedOptionIds(
                question.type === 'MULTI_OPTION'
                    ? currentAnswer.answerJSON?.answer.filter(({ data }) => !!data).map(({ optionId }) => optionId)
                    : [currentAnswer.answerOption.id],
            );

            setOptionsLoaded(true);
        }

        if (questions.filter((alternative) => alternative.id === question.id).length === 0 && alternatives.length > 0) {
            setQuestions((prevState): any => [...prevState, { answerOption: alternatives, id: question.id }]);
        }
    }, [currentAnswer, alternatives]);

    useEffect(() => {
        const list = questions.filter((alternative) => alternative.id === question.id);
        if (question && question.options) {
            if (list.length === 0) {
                if (questionnaire.evaluation.shuffleAlternatives) {
                    setAlternatives(shuffle(question.options));
                } else {
                    setAlternatives(question.options.sort((a, b) => a.answerOptionOrder - b.answerOptionOrder));
                }
            } else {
                setAlternatives(list[0].answerOption);
            }
        }
    }, [question]);

    useEffect(() => {
        if (successOnFinishQuestionnaire) {
            revalidateResult();
        }
    }, [successOnFinishQuestionnaire]);

    useEffect(() => {
        if (questionnaire.score !== null) {
            finished(questionnaire);
        }
    }, [questionnaire?.score]);

    const handleSelect = (option: AnswerOption) => {
        let updateAnswer = {
            ...currentAnswer,
            answerOption: option,
            answeredOn: new Date(),
        } as Answer;

        if (question.type === 'MULTI_OPTION') {
            const optionsAlreadyExists: boolean = !!selectedOptionnIds.find((optionId) => optionId === option.id);
            const optionIds: number[] = optionsAlreadyExists
                ? selectedOptionnIds.filter((optionId) => optionId !== option.id)
                : selectedOptionnIds.concat(option.id);

            setSelectedOptionIds(optionIds);

            const answerJSONDataItem: AnswerJSONDataItem[] = [];

            alternatives.forEach(({ id: optionId }: AnswerOption) => {
                answerJSONDataItem.push({ optionId, data: optionIds.indexOf(optionId) !== -1 });
            });

            updateAnswer = { ...updateAnswer, answerJSON: { answer: answerJSONDataItem } };
        } else {
            setSelectedOptionIds([option.id]);
        }

        setOptionsLoaded(true);
        dispatch(updateAnswerRequest(questionnaire.id, updateAnswer));

        const answerIndex: number = questionnaire.answers.findIndex(({ id: answerId }) => updateAnswer.id === answerId);

        if (answerIndex !== -1) {
            questionnaire.answers[answerIndex] = updateAnswer;
            mutate({ ...questionnaire }, false);

            const { answers } = questionnaire || [];

            const singleOptionAnswered: number = answers.filter(({ answerOption, answerJSON }) => answerOption && !answerJSON).length;
            const multiOptionAnswered: number = answers.filter(({ answerJSON }) => answerJSON?.answer?.some(({ data }) => data)).length;

            setQtyAnswered(singleOptionAnswered + multiOptionAnswered);
        }
    };

    const getProgressPercent = (): number => {
        if (!questionnaire.answers) return 0;
        return (qtyAnswered / questionnaire.answers.length) * 100;
    };

    const scrollToTop = () => {
        ['.options', '.description'].forEach((item) => {
            const element = document.querySelector(item);
            element && element.scrollTo({ top: 0, behavior: 'smooth' });
        });
    };

    const handleNext = () => {
        setCurrentAnswer(questionnaire.answers[answerIndex]);
        setAnswerIndex(answerIndex + 1);
        scrollToTop();
        setOptionsLoaded(false);
    };

    const handlePreviews = () => {
        setCurrentAnswer(questionnaire.answers[answerIndex - 2]);
        setAnswerIndex(answerIndex - 1);
        scrollToTop();
        setOptionsLoaded(false);
    };

    const handleNavigation = (index: number) => {
        setAnswerIndex(index);
        setCurrentAnswer(questionnaire.answers[index - 1]);
        scrollToTop();
    };

    const handleConcluded = () => {
        dispatch(finishQuestionnaireRequest(questionnaire.id));
    };

    const getImagePathThumb = (imagePath: string) => {
        if (imagePath) {
            let start = imagePath.lastIndexOf('.');

            if (start < 0) {
                start = imagePath.length;
            }

            return imagePath.substring(0, start) + '_thumb' + imagePath.substring(start);
        }

        return '';
    };

    return (
        <>
            <NavigateQuestionModal
                answerIndex={answerIndex}
                setAnswerIndex={(index: number) => handleNavigation(index)}
                setShow={setShowNavigation}
                show={showNavigation}
            />

            <section className="questionnaire-questions container">
                <div className="question-timer">
                    <div className="title" onClick={() => setShowNavigation(true)}>
                        <strong className="question">
                            {strings.QUESTION} {answerIndex} <i className="material-icons">{Constants.icons.KEYBOARD_ARROW_DOWN}</i>
                        </strong>
                        <strong className="quantity">
                            {strings.OF} {questionnaire && questionnaire.answers ? questionnaire.answers.length : 0}
                        </strong>
                    </div>
                    {timeLeft > 0 && <Timer minutes={timeLeft} onEnded={handleConcluded} />}
                </div>

                <div className="question-options">
                    <div className="description">
                        <div dangerouslySetInnerHTML={{ __html: question.description }}></div>

                        {question.imagePath && (
                            <>
                                <img
                                    alt=""
                                    className="image-path-thumb"
                                    onClick={() => setFullscreenImage(true)}
                                    src={getImagePathThumb(question.imagePath)}
                                />
                                <FullScreenImageModal
                                    fullscreen={fullScreenImage}
                                    onCloseImage={() => setFullscreenImage(false)}
                                    path={question.imagePath}
                                />
                            </>
                        )}
                    </div>

                    <div className="options">
                        <label>{`${
                            question.type === 'SINGLE_OPTION' ? strings.SELECT_CORRECT_ALTERNATIVE : strings.SELECT_CORRECT_ALTERNATIVES
                        }:`}</label>
                        {alternatives.map((item, index) => (
                            <div
                                className={`option ${!!selectedOptionnIds.find((optionId) => optionId === item.id) ? 'selected' : ''}`}
                                key={`opt-${index}`}
                                onClick={() => handleSelect(item)}
                            >
                                {item.description}
                            </div>
                        ))}
                    </div>
                </div>

                <FloatingCard>
                    <div></div>
                    <div className="navigation">
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${getProgressPercent()}%` }}></div>
                            <label>
                                {qtyAnswered} {qtyAnswered === 0 || qtyAnswered > 1 ? strings.QUESTIONS_ANSWERED : strings.QUESTION_ANSWERED}
                            </label>
                        </div>
                        <NavButton disabled={answerIndex === 1} onClick={handlePreviews} type="btn-light mr-2">
                            {strings.PREVIOUS}
                        </NavButton>
                        <NavButton
                            disabled={questionnaire && questionnaire.answers && answerIndex === questionnaire.answers.length}
                            onClick={handleNext}
                            type={`${getProgressPercent() === 100 ? 'btn-light mr-2' : 'btn-primary'}`}
                        >
                            {strings.NEXT}
                        </NavButton>
                        {getProgressPercent() === 100 && <NavButton onClick={handleConcluded}>{strings.CONCLUDE}</NavButton>}
                    </div>
                </FloatingCard>
            </section>
        </>
    );
};

export default QuestionnaireQuestions;
